/**
 * usePollingController — a visibility-aware, backing-off poll loop.
 *
 * Built to stop the benchmark views from hammering the backend:
 *   - Pauses entirely while the tab is hidden (`document.hidden`); on the tab
 *     becoming visible again it fires one immediate tick and resumes.
 *   - Backs off: after a tick that reports "no change" the interval doubles up
 *     to `maxMs`; any tick reporting a change (or an explicit `bump()`) resets
 *     it to `baseMs`. Stalled/idle views settle at the slow cadence; active
 *     ones stay responsive.
 *
 * @param {() => (boolean | Promise<boolean|void>)} tick  Polling fn. Return
 *   `true` when something changed (resets the interval to `baseMs`).
 * @param {{ baseMs?: number, maxMs?: number }} [options]
 * @returns {{ start: (opts?: {immediate?: boolean}) => void, stop: () => void,
 *   bump: () => void, isActive: import('vue').Ref<boolean> }}
 */
import { getCurrentInstance, onBeforeUnmount, ref } from 'vue';

const isHidden = () => typeof document !== 'undefined' && document.hidden;
const canListen = () => typeof document !== 'undefined' && typeof document.addEventListener === 'function';

export function usePollingController(tick, { baseMs = 8000, maxMs = 30000 } = {}) {
  let timer = null;
  let currentMs = baseMs;
  let stopped = true;
  let running = false;
  const isActive = ref(false);

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = () => {
    clearTimer();
    if (stopped || isHidden()) return; // visibility handler resumes when shown
    timer = setTimeout(run, currentMs);
  };

  const runSoon = () => {
    clearTimer();
    if (stopped || isHidden()) return;
    timer = setTimeout(run, 0);
  };

  async function run() {
    if (stopped || running) return;
    running = true;
    let changed = false;
    try {
      changed = (await tick()) === true;
    } catch {
      // Swallow — a failed tick must not kill the loop.
    } finally {
      running = false;
    }
    currentMs = changed ? baseMs : Math.min(maxMs, currentMs * 2);
    schedule();
  }

  const onVisibilityChange = () => {
    if (stopped) return;
    if (isHidden()) {
      clearTimer();
    } else {
      currentMs = baseMs; // come back fresh + responsive
      runSoon();
    }
  };

  function start({ immediate = true } = {}) {
    if (!stopped) return;
    stopped = false;
    currentMs = baseMs;
    isActive.value = true;
    if (canListen()) document.addEventListener('visibilitychange', onVisibilityChange);
    if (immediate) runSoon();
    else schedule();
  }

  function stop() {
    if (stopped) return;
    stopped = true;
    isActive.value = false;
    clearTimer();
    if (canListen()) document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  /** Force the next tick to run promptly and reset the backoff to baseMs. */
  function bump() {
    currentMs = baseMs;
    if (!stopped) runSoon();
  }

  // Auto-clean when used inside a component/composable setup scope.
  if (getCurrentInstance()) onBeforeUnmount(stop);

  return { start, stop, bump, isActive };
}
